import { useCallback, useMemo, useState } from 'react'
import { unionBy } from 'lodash'
import { DateTime } from 'luxon'
import {
  ChatMessageDirection,
  ChatMessageLinkType,
  FeedOrder,
  LabOrderStatus,
  SmsMessageDirection,
  SmsMessageStatus,
  VideoCallCancelledBy,
  VideoCallType,
} from 'src/graphql.generated'
import {
  getHealthQuestionnaireScores,
  getMeditationMinutes,
  getSectionFormat,
  HQ_SCORE_TAGS,
  SectionFormat,
} from './patientResponseFormat'
import { toIso } from '../format'
import {
  AdvancedPatientResponseItemFragment,
  GetAdvancedRoleChatMessagesQuery,
  GetPatientConversationDocument,
  GetPatientConversationQuery,
  useCreatePatientChatMessageMutation,
  useCreatePatientRoleChatMessageMutation,
  useEditPatientChatMessageMutation,
  useGetAdvancedRoleChatMessagesQuery,
  useGetPatientConversationQuery,
} from './patientConversation.generated'

export const CONVERSATION_PAGE_SIZE = 50

export type LessonResponseSectionEntry = {
  entryID: string
  question: string | null
  rawValue: unknown
  tag: string | null
  // Standard's `SectionEntryWithContext` requires the `__typename`.
  entryContext: {
    __typename: 'PatientResponseSectionEntryContextSingleChoice'
    options: Array<{ value: string; label: string }>
  } | null
}

export type LessonResponseSection = {
  sectionID: string
  title: string | null
  format: SectionFormat
  // This section's own questionnaire score; `ActivityContext.summary` scores the whole response.
  score: number | null
  entries: LessonResponseSectionEntry[]
}

export type ActivityContext = {
  title: string
  summary: string | null
  programID: string | null
  sections: LessonResponseSection[]
}

export type ActivityFacts =
  | {
      kind: 'videoCall'
      startTime: string
      endTime: string
      cancelledAt: string | null
      cancelledBy: VideoCallCancelledBy | null
      coachID: string
    }
  | {
      kind: 'labOrder'
      orderID: string
      status: LabOrderStatus
      events: Array<{ status: string; occurredAt: string }>
    }
  | { kind: 'overdueQuestionnaire'; lessonID: string; moduleNumber: number }

export type ChatLink = { type: ChatMessageLinkType; id: string }

export type ChatMessageFile = { fileID: string; filename: string; contentType: string; downloadURL: string | null }

// The response or lesson a message was sent about, which Standard's `ChatMessageItem` previews above the text.
export type ChatMessageLink = { title: string; category: string | null; createdAt: string | null }

export type ConversationItem =
  | {
      kind: 'chat'
      id: string
      messageID: string
      text: string
      createdAt: string
      coachID: string | null
      direction: ChatMessageDirection
      files: ChatMessageFile[]
      linkedTo: ChatMessageLink | null
    }
  | {
      kind: 'sms'
      id: string
      text: string
      createdAt: string
      coachID: string | null
      direction: SmsMessageDirection
      status: SmsMessageStatus
    }
  | {
      kind: 'activity'
      id: string
      createdAt: string
      label: string
      activityType: 'lessonResponse' | 'videoCall' | 'labOrder' | 'overdueQuestionnaire'
      coachID: string | null
      responseID: string | null
      context: ActivityContext | null
      facts: ActivityFacts | null
      iconCategory: string
      continued: boolean
      isCompleted: boolean
    }

type ActivityItem = Extract<ConversationItem, { kind: 'activity' }>
type FeedItem = GetPatientConversationQuery['feed']['items'][number]
type ChatFeedItem = Extract<FeedItem, { __typename: 'ChatMessage' }>
type ActivityFeedItem = Exclude<FeedItem, { __typename: 'ChatMessage' | 'SmsMessage' }>

const MEDITATION_CATEGORIES = new Set(['MEDITATION_HRV', 'MEDITATION_MINDFULNESS'])
const DURATION_CATEGORIES = new Set(['CBT', 'VIDEO_RECAP', 'VIDEO_TIPS', 'VIDEO_INTRODUCTION'])

// Standard's `getResponseSummary` (`ResponseHeader.tsx`), over the shared scoring.
const responseSummary = (item: AdvancedPatientResponseItemFragment): string | null => {
  if (!item.isCompleted) {
    return 'Incomplete practice'
  }
  if (item.category === 'HEALTH_QUESTIONNAIRE') {
    const scores = getHealthQuestionnaireScores(item)
    return scores.length > 0 ? scores.map((score) => `${score.label} ${score.value}`).join(' - ') : null
  }
  if (item.category && MEDITATION_CATEGORIES.has(item.category)) {
    const minutes = getMeditationMinutes(item) || item.duration
    return minutes ? `${minutes} min meditation` : null
  }
  if (item.category && DURATION_CATEGORIES.has(item.category)) {
    return item.duration ? `${item.duration} min` : null
  }
  return null
}

const toSections = (sections: AdvancedPatientResponseItemFragment['sections']): LessonResponseSection[] => {
  const scores = getHealthQuestionnaireScores({ sections })

  return sections
    .filter((section) => section.entries.length > 0)
    .map((section) => ({
      sectionID: section.sectionID,
      title: section.title ?? null,
      format: getSectionFormat(section.type),
      score: scores.find((candidate) => candidate.type === section.type)?.value ?? null,
      entries: section.entries
        .filter((entry) => !HQ_SCORE_TAGS.has(entry.tag ?? ''))
        .map((entry) => ({
          entryID: entry.entryID,
          question: entry.question ?? null,
          rawValue: entry.rawValue,
          tag: entry.tag ?? null,
          entryContext: entry.entryContext
            ? { __typename: 'PatientResponseSectionEntryContextSingleChoice', options: entry.entryContext.options }
            : null,
        })),
    }))
}

const lessonResponse = (
  response: AdvancedPatientResponseItemFragment,
  responseID: string,
  continued: boolean
): Omit<ActivityItem, 'kind' | 'id' | 'createdAt'> => {
  const title = response.title ?? response.lessonID

  return {
    label: title,
    activityType: 'lessonResponse',
    coachID: null,
    responseID,
    context: {
      title,
      summary: responseSummary(response),
      programID: response.programID,
      sections: toSections(response.sections),
    },
    facts: null,
    iconCategory: response.category ?? 'DEFAULT',
    continued,
    isCompleted: response.isCompleted,
  }
}

const toActivity = (item: ActivityFeedItem): Omit<ActivityItem, 'kind' | 'id' | 'createdAt'> => {
  const withoutDetail = { responseID: null, context: null, continued: false }

  switch (item.__typename) {
    case 'PatientResponse':
      return lessonResponse(item, item.itemID, false)
    case 'ResumedPatientResponse':
      return lessonResponse(item.resumedResponse, item.resumedResponse.responseID, true)
    case 'VideoCall':
      return {
        label: `${item.callType === VideoCallType.Intake ? 'Intake' : 'Check-in'} call scheduled`,
        activityType: 'videoCall',
        coachID: item.coach.id,
        iconCategory: 'VIDEO_CALL_SCHEDULED',
        isCompleted: true,
        ...withoutDetail,
        facts: {
          kind: 'videoCall',
          startTime: toIso(item.startTime) ?? '',
          endTime: toIso(item.endTime) ?? '',
          cancelledAt: toIso(item.cancelledAt),
          cancelledBy: item.cancelledBy ?? null,
          coachID: item.coach.id,
        },
      }
    case 'LabOrderEvent':
      return {
        label: 'Lab order',
        activityType: 'labOrder',
        coachID: item.coachID ?? null,
        iconCategory: 'LAB_ORDER',
        isCompleted: true,
        ...withoutDetail,
        facts: {
          kind: 'labOrder',
          orderID: item.orderID,
          status: item.orderStatus,
          events: item.events.map((event) => ({ status: event.status, occurredAt: toIso(event.occuredAt) ?? '' })),
        },
      }
    case 'OverdueQuestionnaire':
      return {
        label: 'Health questionnaire',
        activityType: 'overdueQuestionnaire',
        coachID: null,
        iconCategory: 'HEALTH_QUESTIONNAIRE',
        isCompleted: false,
        ...withoutDetail,
        facts: {
          kind: 'overdueQuestionnaire',
          lessonID: item.questionnaireLessonID,
          moduleNumber: item.moduleNumber,
        },
      }
    default: {
      const unhandled: never = item
      return unhandled
    }
  }
}

// Standard's precedence: a linked response before a linked lesson.
const chatMessageLink = ({ linkedResponse, linkedLesson }: ChatFeedItem): ChatMessageLink | null => {
  if (linkedResponse) {
    return {
      title: linkedResponse.title ?? linkedResponse.lessonID,
      category: linkedResponse.category ?? null,
      createdAt: toIso(linkedResponse.createdAt),
    }
  }
  if (linkedLesson) {
    return {
      title: linkedLesson.title ?? linkedLesson.lessonID,
      category: linkedLesson.category ?? null,
      createdAt: null,
    }
  }
  return null
}

const toConversationItem = (item: FeedItem): ConversationItem => {
  const createdAt = toIso(item.createdAt) ?? ''

  if (item.__typename === 'ChatMessage') {
    return {
      kind: 'chat',
      id: item.itemID,
      messageID: item.messageID,
      text: item.text,
      createdAt,
      coachID: item.coachID ?? null,
      direction: item.chatMessageDirection,
      files: (item.files ?? []).map((file) => ({ ...file, downloadURL: file.downloadURL ?? null })),
      linkedTo: chatMessageLink(item),
    }
  }
  if (item.__typename === 'SmsMessage') {
    return {
      kind: 'sms',
      id: item.itemID,
      text: item.text,
      createdAt,
      coachID: item.coachID ?? null,
      direction: item.smsMessageDirection,
      status: item.status,
    }
  }
  return { kind: 'activity', id: item.itemID, createdAt, ...toActivity(item) }
}

export const getConversationItems = (data: GetPatientConversationQuery | undefined): ConversationItem[] =>
  data?.feed.__typename === 'FeedSuccess' ? data.feed.items.map(toConversationItem) : []

const byCreatedAt = (a: ConversationItem, b: ConversationItem) =>
  DateTime.fromISO(a.createdAt).toMillis() - DateTime.fromISO(b.createdAt).toMillis()

const getRoleChatItems = (data: GetAdvancedRoleChatMessagesQuery | undefined): ConversationItem[] =>
  data?.advancedRoleChatMessages.map(toConversationItem).sort(byCreatedAt) ?? []

// Overlap the exclusive `after` bound so role messages at the feed boundary are included.
// `undefined` once a page came back short, so nothing older is left to load.
const feedWindowStart = (page: ConversationItem[]): string | undefined =>
  page.length < CONVERSATION_PAGE_SIZE
    ? undefined
    : DateTime.fromISO([...page].sort(byCreatedAt)[0].createdAt)
        .minus({ milliseconds: 1 })
        .toISO() ?? undefined

const sentByCareTeam = (item: ConversationItem): boolean =>
  (item.kind === 'chat' && item.direction === ChatMessageDirection.ToPatient) ||
  (item.kind === 'sms' && item.direction === SmsMessageDirection.ToPatient)

// Standard's `isNewItem` (`FeedItems.tsx`).
export const isUnread = (item: ConversationItem, lastSeenAt: string | null | undefined): boolean =>
  !!lastSeenAt && !sentByCareTeam(item) && DateTime.fromISO(lastSeenAt) < DateTime.fromISO(item.createdAt)

// `undefined` while the marker loads and `null` once it is known to be absent, as in `ParticipantFeedCombined`.
export const unreadBoundaryKey = (lastSeenAt: string | null | undefined): number | null | undefined => {
  if (lastSeenAt === undefined) {
    return undefined
  }
  return lastSeenAt ? DateTime.fromISO(lastSeenAt).toMillis() : null
}

// The paging half of Standard's `useFeedItemsQuery`.
export type ConversationPaging = {
  hasOlder: boolean
  isFetchingOlder: boolean
  fetchMoreOlder: () => Promise<void>
  fetchMoreNewer: () => Promise<void>
}

type ConversationOptions = {
  // The shared thread is the therapist's chat; a provider with no therapist role has no business reading it here.
  hideSharedChat?: boolean
}

export const usePatientConversation = (uid: string, { hideSharedChat = false }: ConversationOptions = {}) => {
  const [exhaustedFor, setExhaustedFor] = useState<string | null>(null)
  const [isFetchingOlder, setIsFetchingOlder] = useState(false)
  // Latched on the first load, like Standard's `useFeedItemsQuery`, so a refetch can't move the marker mid-visit.
  const [seen, setSeen] = useState<{ uid: string; lastSeenAt: string | null }>()
  // Latched the same way: role chats start where the first feed page does, then page alongside the feed.
  const [roleWindow, setRoleWindow] = useState<{ uid: string; after: string | undefined }>()
  // Keep a failed role page available for retry even though Apollo has already merged its feed page.
  const [pendingOlderRolePage, setPendingOlderRolePage] = useState<{
    uid: string
    after: string | undefined
    before: string
  }>()

  const variables = useMemo(() => ({ uid, limit: CONVERSATION_PAGE_SIZE, order: FeedOrder.Desc }), [uid])

  const query = useGetPatientConversationQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setSeen((current) => (current?.uid === uid ? current : { uid, lastSeenAt: toIso(data.feed.lastSeenAt) }))
      setRoleWindow((current) =>
        current?.uid === uid ? current : { uid, after: feedWindowStart(getConversationItems(data)) }
      )
    },
  })

  const refetchQueries = [{ query: GetPatientConversationDocument, variables }]
  const [createChat, chatMutation] = useCreatePatientChatMessageMutation({ refetchQueries })
  const [createRoleChat, roleChatMutation] = useCreatePatientRoleChatMessageMutation()
  const [editChatMessage, editMutation] = useEditPatientChatMessageMutation({ refetchQueries })

  const feedItems = useMemo(() => getConversationItems(query.data), [query.data])
  const feedHasOlder = exhaustedFor !== uid && feedItems.length >= CONVERSATION_PAGE_SIZE

  const roleVariables = useMemo(
    () =>
      roleWindow?.uid === uid ? { uid, after: roleWindow.after ? new Date(roleWindow.after) : undefined } : undefined,
    [roleWindow, uid]
  )
  const roleQuery = useGetAdvancedRoleChatMessagesQuery({
    variables: roleVariables ?? { uid },
    skip: !roleVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const { fetchMore: fetchMoreRoleQuery } = roleQuery
  const roleItems = useMemo(() => getRoleChatItems(roleQuery.data), [roleQuery.data])

  const fetchMoreRoleChats = useCallback(
    async (range: { after: string | undefined; before: string | undefined }) => {
      if (!roleVariables) {
        return
      }
      await fetchMoreRoleQuery({
        variables: range,
        updateQuery: (previous, { fetchMoreResult }) => ({
          advancedRoleChatMessages: unionBy(
            previous.advancedRoleChatMessages,
            fetchMoreResult.advancedRoleChatMessages,
            'messageID'
          ),
        }),
      })
    },
    [fetchMoreRoleQuery, roleVariables]
  )

  // A failed refresh is retried by the next one; it must not fail the feed's refresh or a send that went through.
  const fetchNewerRoleChats = useCallback(
    () =>
      fetchMoreRoleChats({
        after: roleItems[roleItems.length - 1]?.createdAt ?? roleWindow?.after,
        before: undefined,
      }).catch(() => undefined),
    [fetchMoreRoleChats, roleItems, roleWindow]
  )

  // Stable across renders — `useChatEventFeed` memoizes the whole sorted feed on this identity.
  const items = useMemo(() => {
    // Paging above still counts the whole feed; only what is shown drops the shared thread's in-app messages.
    const shownFeedItems = hideSharedChat ? feedItems.filter((item) => item.kind !== 'chat') : feedItems

    return roleItems.length === 0 ? shownFeedItems : [...shownFeedItems, ...roleItems].sort(byCreatedAt)
  }, [feedItems, hideSharedChat, roleItems])

  const fetchMoreOlder = async () => {
    const oldest = feedItems[0]?.createdAt
    if (!oldest) {
      return
    }
    setIsFetchingOlder(true)
    try {
      let page = pendingOlderRolePage?.uid === uid ? pendingOlderRolePage : undefined
      if (!page) {
        const { data } = await query.fetchMore({ variables: { before: oldest } })
        page = { uid, after: feedWindowStart(getConversationItems(data)), before: oldest }
        setPendingOlderRolePage(page)
      }
      await fetchMoreRoleChats({ after: page.after, before: page.before })
      if (!page.after) {
        setExhaustedFor(uid)
      }
      setPendingOlderRolePage(undefined)
    } finally {
      setIsFetchingOlder(false)
    }
  }

  const fetchMoreNewer = async () => {
    await Promise.all([
      query.fetchMore({ variables: { after: feedItems[feedItems.length - 1]?.createdAt, limit: null } }),
      fetchNewerRoleChats(),
    ])
  }

  const setLastSeenAt = useCallback((lastSeenAt: string | null) => setSeen({ uid, lastSeenAt }), [uid])

  const sendChat = useCallback(
    async (text: string, linksTo: ChatLink | null = null, fileIDs: string[] = [], role: string | null = null) => {
      if (role) {
        await createRoleChat({ variables: { uid, text, linksTo, fileIDs, role } })
        await fetchNewerRoleChats()
      } else {
        await createChat({ variables: { uid, text, linksTo, fileIDs } })
      }
    },
    [createChat, createRoleChat, fetchNewerRoleChats, uid]
  )
  const editChat = useCallback(
    async (messageIDHash: string, text: string) => {
      await editChatMessage({ variables: { uid, messageIDHash, text } })
    },
    [editChatMessage, uid]
  )

  return {
    items,
    hasOlder: feedHasOlder || pendingOlderRolePage?.uid === uid,
    isLoaded: query.data !== undefined,
    isReadyToMarkSeen:
      query.data !== undefined &&
      !query.loading &&
      !query.error &&
      roleVariables !== undefined &&
      roleQuery.data !== undefined &&
      !roleQuery.loading &&
      !roleQuery.error,
    isFetchingOlder,
    fetchMoreOlder,
    fetchMoreNewer,
    lastSeenAt: seen?.uid === uid ? seen.lastSeenAt : undefined,
    setLastSeenAt,
    inAppChatEnabled: query.data?.patient.chatMessagingEnabled === true,
    sendChat,
    editChat,
    sending: chatMutation.loading || roleChatMutation.loading || editMutation.loading,
    error: query.error ?? roleQuery.error,
  }
}
